import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import { DetailsLayout, LabelValue, JobSummary } from "../components/bifrost"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Grid, Box, Tooltip, Button, FormControl, InputLabel, Select, MenuItem, Chip, Tabs, Tab } from "@material-ui/core"
import { getUploadJobDetailsRequest } from "../actions/bifrostActions"
import { getFormattedTime, getStatusColor, useInterval } from "../utils"
import TableView from "../components/common/TableView/TableView"
import { getFileFieldValues } from "../services/bifrostServices"
import { useTranslation } from "react-i18next"
import WithDrawer from "../components/common/drawer/withDrawer"
const useStyles = makeStyles((theme) => ({
	jobDetails: {
		"& .MuiChip-root": {
			borderRadius: "4px",
		},
		"& .MuiDataGrid-cell": {
			padding: "0 16px",
		},
	},
	actionDiv: {
		display: "flex",
		justifyContent: "space-between",
	},
	primaryBtn: {
		textTransform: "initial",
		fontSize: theme.typography.fontSize,
		padding: "8px 12px",
		color: theme.palette.primary.dark,
	},
	formControl: {
		margin: theme.spacing(1),
		width: 140,
		"& .MuiSelect-icon": {
			color: theme.palette.common.black,
		},
		"& .MuiSelect-outlined": {
			backgroundColor: theme.palette.grey[100],
			// border: `solid 1px ${theme.palette.grey.D400}`,
			borderRadius: "5px",
		},
		"& .MuiOutlinedInput-notchedOutline": {
			border: "transparent",
		},
	},
	tabs: {
		color: theme.palette.primary.dark,
		"& .MuiTabs-indicator": {
			backgroundColor: theme.palette.primary.dark,
		},
		"& .MuiTab-root": {
			textTransform: "initial",
		},
	},
}))
const PageUploadDetails = () => {
	const dispatch = useDispatch()
	const [t] = useTranslation("global")
	const classes = useStyles()
	const history = useHistory()
	const { caseId, jobId } = useParams()
	const theme = useTheme()

	const uploadJobDetails = useSelector((state) => state.bifrostUploadDetails.uploadJobDetails)
	const isPageLoading = useSelector((state) => state.bifrostUploadDetails.isLoading)

	const [pageNumber, setPageNumber] = React.useState(1)
	const [pageSize, setPageSize] = React.useState(10)
	const [fileStatus, setFileStatus] = React.useState("")
	const [fileType, setFileType] = React.useState("")
	const [fileStatusList, setFileStatusList] = React.useState([])
	const [fileTypesList, setFileTypeList] = React.useState([])
	const [tabValue, setTabValue] = React.useState(0)

	const MenuProps = {
		PaperProps: {
			style: {
				maxWidth: 216,
				maxHeight: 300,
			},
		},
		anchorOrigin: {
			vertical: "bottom",
			horizontal: "left",
		},
		getContentAnchorEl: null,
	}

	useEffect(() => {
		getUploadDetails()
		getFileStatusList()
		getFileTypeList()
	}, [])

	useEffect(() => {
		getUploadDetails()
	}, [pageNumber, pageSize])

	useEffect(() => {
		getUploadDetails(true)
	}, [fileStatus, fileType])

	useInterval(
		() => {
			if (uploadJobDetails?.status !== "Successful") {
				getUploadDetails()
			}
		},
		15000,
		false,
		[]
	)

	const getUploadDetails = (resetPage) => {
		const payload = {
			jobId: jobId,
			filters: {
				file_status: fileStatus,
				file_type: fileType,
			},
			page_number: resetPage ? 1 : pageNumber,
			page_size: pageSize,
		}
		dispatch(getUploadJobDetailsRequest(payload))
	}

	const getFileStatusList = async () => {
		const result = await getFileFieldValues(jobId, caseId, "jobs.file_status")
		setFileStatusList(result.data.unique_values)
	}

	const getFileTypeList = async () => {
		const result = await getFileFieldValues(jobId, caseId, "original_file_type")
		setFileTypeList(result.data.unique_values)
	}

	const getActionItems = () => {
		return (
			<div className={classes.actionDiv}>
				<Button
					// color="primary"
					onClick={() => {
						history.push(`/bifrost/case/${caseId}`)
					}}
					className={classes.primaryBtn}
				>
					{t("bifrost.buttonTexts.back")}
				</Button>
			</div>
		)
	}

	const getRows = () => {
		return uploadJobDetails?.files
			? uploadJobDetails.files.map((file) => ({
					id: file.file_id,
					name: file.name,
					original_file_type: file.original_file_type,
					size: file.human_readable_size || 0,
					file_status: file.jobs[0]?.file_status,
			  }))
			: []
	}

	const getToolTipSection = (value) => {
		return (
			<Tooltip title={value} placement="top">
				<Box style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{value}</Box>
			</Tooltip>
		)
	}

	const getColumns = () => {
		return [
			{
				field: "name",
				headerName: t("bifrost.table_headers.fileName"),
				sortable: false,
				resizable: true,
				width: 550,
				renderCell: (params) => getToolTipSection(params?.row?.name),
			},
			{
				field: "original_file_type",
				headerName: t("bifrost.table_headers.fileType"),
				sortable: false,
				width: 300,
			},
			{
				field: "size",
				headerName: t("bifrost.table_headers.fileSize"),
				sortable: false,
				width: 300,
			},
			{
				field: "file_status",
				headerName: t("bifrost.table_headers.fileStatus"),
				sortable: false,
				width: 250,
				renderCell: (params) => (
					<Chip
						label={params?.row?.file_status}
						size="small"
						style={{
							color: getStatusColor(params?.row?.file_status, theme).color,
							backgroundColor: getStatusColor(params?.row?.file_status, theme).background,
						}}
					/>
				),
			},
		]
	}

	const handleFileStatusChange = (event) => {
		setFileStatus(event.target.value)
	}
	const handleFileTypeChange = (event) => {
		setFileType(event.target.value)
	}

	const handlePageNumberChange = (page) => {
		setPageNumber(page)
	}
	const handlePageSizeChange = (pageSize) => {
		setPageSize(pageSize)
	}

	const getFileStatusFilter = () => {
		return (
			<>
				<FormControl fullWidth className={classes.formControl} variant="outlined" size="small">
					<InputLabel id="file_status-select-label">{t("bifrost.filters.fileStatus")}</InputLabel>
					<Tooltip placement="top" arrow title={fileStatus}>
						<Select
							labelId="file-status-select-label"
							id="file-status-select"
							value={fileStatus}
							label={t("bifrost.filters.fileStatus")}
							onChange={handleFileStatusChange}
							MenuProps={MenuProps}
						>
							<MenuItem value="">
								<em>{t("bifrost.file_status.all")}</em>
							</MenuItem>
							{fileStatusList.map((fileStatus) => {
								return (
									<MenuItem key={fileStatus} value={fileStatus}>
										{fileStatus}
									</MenuItem>
								)
							})}
						</Select>
					</Tooltip>
				</FormControl>
			</>
		)
	}

	const getFileTypeFilter = () => {
		return (
			<>
				<FormControl fullWidth className={classes.formControl} variant="outlined" size="small">
					<InputLabel id="file-type-select-label">{t("bifrost.filters.fileType")}</InputLabel>
					<Tooltip placement="top" arrow title={fileType}>
						<Select
							labelId="file-type-select-label"
							id="file-type-select"
							value={fileType}
							label={t("bifrost.filters.fileType")}
							onChange={handleFileTypeChange}
							MenuProps={MenuProps}
						>
							<MenuItem value="">
								<em>{t("bifrost.file_types.all")}</em>
							</MenuItem>
							{fileTypesList.map((fileType) => {
								return (
									<MenuItem key={fileType} value={fileType}>
										{fileType}
									</MenuItem>
								)
							})}
						</Select>
					</Tooltip>
				</FormControl>
			</>
		)
	}

	const getListView = () => {
		return (
			<TableView
				columns={getColumns()}
				rows={getRows()}
				isLoading={isPageLoading}
				pageInfo={{
					page: pageNumber,
					pageSize: pageSize,
					rowsPerPageOptions: [5, 10, 20, 50],
					totalRowCount: uploadJobDetails?.pagination?.total_count,
					paginationMode: "server",
					handlePageChange: handlePageNumberChange,
					handlePageSizeChange: handlePageSizeChange,
				}}
				sortInfo={{
					sortingMode: "server",
					sortModel: [],
					onSortModelChange: () => {},
				}}
				checkboxSelection={false}
				footerInfo={{
					hideFooter: false,
					hideSelectionCount: true,
					hidePagination: false,
				}}
				topBarInfo={{
					show: true,
					leftSection: "All files",
					rightSection: (
						<>
							{getFileTypeFilter()} {getFileStatusFilter()}
						</>
					),
				}}
				totalCountText={
					!!uploadJobDetails?.pagination?.total_count &&
					`${t("tableColumns.totalFiles")}:  ${uploadJobDetails?.pagination?.total_count || 0}`
				}
				showDownloadButton={false}
				showColumnsButton={false}
			/>
		)
	}

	const getJobSummaryView = () => {
		const chartData = [
			[
				"File type",
				"count",
				{ role: "style" },
				// { role: "annotation" },
				{ role: "tooltip" },
			],
		]
		// For supported file type
		uploadJobDetails.bfiles_summary?.supported?.forEach((summaryObj) => {
			const typeData = [
				summaryObj?.type,
				summaryObj?.virtual_bfiles_count,
				theme.palette.barGraph.B10,
				`${summaryObj?.type}(${summaryObj?.virtual_bfiles_count}, ${summaryObj?.virtual_bfiles_size})`,
			]
			chartData.push(typeData)
		})
		// For unsupported file type
		uploadJobDetails.bfiles_summary?.unsupported?.forEach((summaryObj) => {
			const typeData = [
				summaryObj?.type,
				summaryObj?.virtual_bfiles_count,
				theme.palette.barGraph.B30,
				`${summaryObj?.type}(${summaryObj?.virtual_bfiles_count}, ${summaryObj?.virtual_bfiles_size})`,
			]
			chartData.push(typeData)
		})
		return (
			<JobSummary
				data={chartData}
				loading={isPageLoading}
				title={t("bifrost.titles.overallCountUploaded")}
				legends={[
					{ text: t("bifrost.charts.supportedFileTypes"), color: theme.palette.barGraph.B10 },
					{ text: t("bifrost.charts.unSupportedFileTypes"), color: theme.palette.barGraph.B30 },
				]}
				axisTitle={{ x: t("bifrost.charts.fileTypes"), y: t("bifrost.charts.counts") }}
			/>
		)
	}
	const handleTabChange = (event, newValue) => {
		setTabValue(newValue)
	}
	const getTabs = () => {
		const tabs = [
			{ label: "Files", value: 0, view: getListView() },
			{ label: "Job summary", value: 1, view: getJobSummaryView() },
		]
		return (
			<Tabs value={tabValue} className={classes.tabs} onChange={handleTabChange} aria-label="abs view">
				<Tab label={t("bifrost.charts.files")} />
				<Tab label={t("bifrost.charts.jobSummary")} />
			</Tabs>
		)
	}

	return (
		<div data-cy="job-details" className={classes.jobDetails}>
			<DetailsLayout
				loading={isPageLoading}
				titleProps={{
					title: t("bifrost.titles.uploadJobDetails"),
					name: uploadJobDetails?.name,
					status: uploadJobDetails?.status,
				}}
				actionItems={getActionItems()}
				listView={tabValue === 0 ? getListView() : getJobSummaryView()}
				showActionsView={true}
				leftActions={getTabs()}
				//**To do: To move filters above table**/
				// rightActions={
				// 	<>
				// 		{getFileTypeFilter()} {getFileStatusFilter()}
				// 	</>
				// }
			>
				<>
					<Grid item xs={9}>
						<LabelValue
							label={t("bifrost.metadataKeys.sourceLocation")}
							value={uploadJobDetails?.upload_config?.source_location?.root_path}
							loading={isPageLoading}
						/>
					</Grid>
					<Grid item xs={3}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={7}>
								<LabelValue
									label={t("bifrost.metadataKeys.jobTriggerDate")}
									value={!!uploadJobDetails?.create_time ? new Date(uploadJobDetails?.create_time).toLocaleString() : "—"}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={5}>
								<LabelValue
									label={t("bifrost.metadataKeys.totalTime")}
									value={
										uploadJobDetails?.total_time === 0
											? t("bifrost.metadataKeys.calculating")
											: getFormattedTime(uploadJobDetails?.total_time)
									}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={9}>
						<Grid container item direction="row" xs={9}>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.processStatus")}
									value={uploadJobDetails.status}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.totalFileSize")}
									value={uploadJobDetails?.bfiles_summary?.total_bfiles_size}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={3}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={7}>
								<LabelValue
									label={t("bifrost.metadataKeys.jobTriggeredBy")}
									value={uploadJobDetails.created_by_username}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={5}>
								<LabelValue
									label={t("bifrost.metadataKeys.uploadTime")}
									value={
										uploadJobDetails?.upload_time === 0
											? t("bifrost.metadataKeys.calculating")
											: getFormattedTime(uploadJobDetails?.upload_time)
									}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={9}>
						<Grid container item direction="row" xs={9}>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.totalFileCount")}
									value={uploadJobDetails?.execution_status?.total || 0}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.completedFiles")}
									value={uploadJobDetails?.execution_status?.summary?.Done || 0}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={3}>
								<LabelValue
									label={t("bifrost.metadataKeys.failedFiles")}
									value={uploadJobDetails?.execution_status?.summary?.Failed || 0}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={3}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={7}>
								{/* <LabelValue label={t("bifrost.metadataKeys.jobTriggeredBy")} value={uploadJobDetails.created_by_username} /> */}
							</Grid>
							<Grid item xs={5}>
								<LabelValue
									label={t("bifrost.metadataKeys.dupTime")}
									value={
										uploadJobDetails?.duplicate_detection_time === 0
											? t("bifrost.metadataKeys.calculating")
											: getFormattedTime(uploadJobDetails?.duplicate_detection_time)
									}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
				</>
			</DetailsLayout>
		</div>
	)
}

export default WithDrawer(PageUploadDetails)
